import { jsx as _jsx } from "react/jsx-runtime";
import merge from 'lodash.merge';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { LOCALES } from '../../locales';
export const DEFAULT_LANG = 'en-GB';
function mergeLocaleWithExtension(extension) {
    return merge({}, LOCALES, extension);
}
export function createLocaleContext({ extendedDictionary, defaultLanguage = DEFAULT_LANG, }) {
    const extendedLocales = mergeLocaleWithExtension(extendedDictionary);
    const LocaleContext = createContext({
        lang: defaultLanguage,
        locales: extendedLocales,
        localesByLang: extendedLocales[defaultLanguage],
    });
    function LocaleProvider({ lang, fallbackLang = defaultLanguage, overrideLocales, children }) {
        const locales = useMemo(() => {
            if (overrideLocales) {
                return merge({}, extendedLocales, overrideLocales);
            }
            return extendedLocales;
        }, [overrideLocales]);
        const localesByLang = useMemo(() => {
            let localesObj = locales[lang];
            if (!localesObj) {
                console.warn(`Snack-uikit: localization for lang ${lang} was not found. Make sure you are using correct lang or passed proper locales to LocaleProvider. For now default language (${DEFAULT_LANG}) will be used`);
                localesObj = locales[fallbackLang];
            }
            return localesObj;
        }, [fallbackLang, lang, locales]);
        return _jsx(LocaleContext.Provider, { value: { lang, locales, localesByLang }, children: children });
    }
    function useLocale(componentName) {
        const { localesByLang, lang } = useContext(LocaleContext);
        const locales = useMemo(() => {
            if (!componentName) {
                return localesByLang;
            }
            return (localesByLang[componentName] || {});
        }, [componentName, localesByLang]);
        const getLocaleText = useCallback(key => {
            let translation = '';
            const complexKey = key.split('.');
            if (complexKey.length === 1) {
                translation = locales[key];
            }
            else {
                translation = complexKey.reduce((acc, cur) => {
                    if (typeof acc === 'string') {
                        return acc;
                    }
                    return acc[cur];
                }, locales);
            }
            if (!(translation === null || translation === void 0 ? void 0 : translation.length)) {
                console.warn(`Snack-uikit: the '${key}' key is not found in the current locale '${lang}'.`);
                return key;
            }
            return translation;
        }, [lang, locales]);
        return {
            t: getLocaleText,
            lang,
        };
    }
    return {
        LocaleContext,
        LocaleProvider,
        useLocale,
    };
}
export const { LocaleProvider, useLocale } = createLocaleContext({});
